import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import theme from '../../../../../app/theme';
import { QuestionAnswerType } from '../../../../../common/utils/questionAnswerTypes';
import { InterviewQuestionProps } from '../../../../../types/InterviewQuestionTypes';
import useSetWatchQuestion from '../../../hooks/useSetWatchQuestion';

export const StyledRadio = styled(Radio)(({ theme }) => ({
  '& .MuiSvgIcon-root': {
    fontSize: 32,
  },
  '&.Mui-checked': {
    width: 32,
    height: 32,
    color: theme.xPalette.completed.color,
    backgroundColor: theme.xPalette.white,
    marginRight: '8px',
  },
  '&.Mui-checked + span': {
    color: theme.xPalette.white,
  },
}));

export enum BinaryValue {
  Yes = 'yes',
  No = 'no',
}

export const radioStyle = {
  flex: 1,
  maxWidth: '50%',
  display: 'flex',
  justifyContent: 'space-between',
  borderRadius: '8px',
  paddingLeft: '16px',
  margin: 0,
  '&.MuiFormControlLabel-root:first-of-type': {
    marginRight: '4px',
  },
};

export const StyledFormLabelYes = styled(FormControlLabel)(({ theme, itemScope }) => ({
  ...radioStyle,
  border: `1px solid ${theme.xPalette.disabled}`,
  '&.MuiFormControlLabel-root': {
    backgroundColor: itemScope ? theme.xPalette.completed.color : '',
  },
}));

export const StyledFormLabelNo = styled(FormControlLabel)(({ theme, itemScope }) => ({
  ...radioStyle,
  border: `1px solid ${theme.xPalette.disabled}`,
  '&.MuiFormControlLabel-root': {
    backgroundColor: itemScope ? theme.xPalette.completed.color : '',
  },
}));

export const StyledFormControl = styled(FormControl)(({ theme, error }) => ({
  margin: '16px 0px',
  '&.MuiFormControl-root .MuiFormGroup-root .MuiFormControlLabel-root': {
    borderColor: error ? theme.palette.error.main : '',
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

export const StyledTypography = styled(Typography)(() => ({
  fontWeight: 'bold',
  marginBottom: '8px',
}));

export default function MinimizeBinaryQuestion({
  question,
  field,
  error,
  helperText,
  isFromGroup,
  setValue,
}: InterviewQuestionProps) {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { handleOnBlur } = useSetWatchQuestion({
    name: field?.name!,
    type: QuestionAnswerType.Binary,
    required: !!question.isRequired,
  });

  const renderAnswers = (BinaryValue: string) => {
    const { displayAnswers, possibleAnswers } = question;

    let items: any = {};
    for (let i = 0; i < possibleAnswers.length; i++) {
      const value = possibleAnswers[i];
      const text = displayAnswers?.length === possibleAnswers?.length
      ? `${displayAnswers?.[i] || value}`.trim()
      : value.trim();

      items = { ...items, [value]: text };
    }

    return items[BinaryValue];
  };

  const handleBinaryChange = (e: any) => {
    const value = e.target?.value;
    setValue(field?.name!, value, { shouldValidate: true });
    handleOnBlur();
  };

  let episode = undefined;
  if (question.name?.includes('current')) {
    episode = t('interview.form.binaryEpisodes.currentEpisode');
  }
  if (question.name?.includes('past')) {
    episode = t('interview.form.binaryEpisodes.pastEpisode');
  }

  return (
    <Box
      marginBottom="16px"
      display={!isFromGroup && isDesktop ? 'flex' : undefined}
      justifyContent={!isFromGroup && isDesktop ? 'space-between' : undefined}
    >
      {!isFromGroup && (
        <Box sx={{ margin: 0, width: isDesktop ? '76%' : '100%' }}>
          {question.questionPrompt && (
            <StyledTypography
              dangerouslySetInnerHTML={{
                __html: question.questionPrompt,
              }}
            ></StyledTypography>
          )}
          <Typography
            dangerouslySetInnerHTML={{
              __html: question.questionText!,
            }}
          ></Typography>
        </Box>
      )}
      <StyledFormControl
        sx={{ margin: 0, width: !isFromGroup && isDesktop ? '22%' : '100%' }}
        error={error}
        variant="standard"
      >
        {episode && (
          <Typography variant="body1" textAlign="center">
            {episode}
          </Typography>
        )}
        <RadioGroup {...field} row onChange={handleBinaryChange}>
          <StyledFormLabelNo
            value={question.possibleAnswers[1] || null}
            itemScope={field?.value === question.possibleAnswers[1]}
            labelPlacement="start"
            control={<StyledRadio checkedIcon={<CheckIcon />} />}
            label={renderAnswers(question.possibleAnswers[1])}
          />
          <StyledFormLabelYes
            value={question.possibleAnswers[0] || null}
            itemScope={field?.value === question.possibleAnswers[0]}
            labelPlacement="start"
            control={<StyledRadio checkedIcon={<CheckIcon />} />}
            label={renderAnswers(question.possibleAnswers[0])}
          />
        </RadioGroup>
        {error && <FormHelperText>{helperText}</FormHelperText>}
      </StyledFormControl>
    </Box>
  );
}
